/**
 * The partner's Django Backoffice account linkage — required before work
 * can be dispatched to them. Reads the lazily-checked status and offers
 * JIT provisioning when the account is missing.
 */

import { Badge, Button, Group, Loader, Stack, Text, Title } from '@mantine/core';

import type { BackofficePartnerStatus } from '@/types/network-partner';

interface BackofficeBadge {
  color: string;
  label: string;
  testId?: string;
}

/** One badge per linkage state: linked, Django unreachable, or missing. */
export function describeBackofficeStatus(status: BackofficePartnerStatus | null): BackofficeBadge {
  if (status?.linked) {
    const pending = status.is_active === false ? ' (invite pending)' : '';
    return {
      color: 'green',
      label: `Linked — user #${status.backoffice_user_id}${pending}`,
      testId: 'np-backoffice-linked',
    };
  }
  if (status && !status.reachable) {
    return { color: 'yellow', label: 'Backoffice unreachable' };
  }
  return { color: 'gray', label: 'Not linked', testId: 'np-backoffice-missing' };
}

/** Provisioning is offered only when Django answered and found no account. */
export function canCreateBackofficeAccount(status: BackofficePartnerStatus | null): boolean {
  return status !== null && status.reachable && !status.linked;
}

interface PartnerIntegrationsPanelProps {
  status: BackofficePartnerStatus | null;
  isLoading: boolean;
  isCreating: boolean;
  /** Banned partners are frozen — no provisioning either. */
  readOnly: boolean;
  onCreateAccount: () => void;
}

export function PartnerIntegrationsPanel({
  status,
  isLoading,
  isCreating,
  readOnly,
  onCreateAccount,
}: Readonly<PartnerIntegrationsPanelProps>) {
  const badge = describeBackofficeStatus(status);
  return (
    <Stack gap="xs">
      <Title order={4}>Integrations</Title>
      <Group gap="sm">
        <Text size="sm" fw={500}>
          Backoffice account:
        </Text>
        {isLoading ? (
          <Loader size="xs" />
        ) : (
          <Badge variant="light" color={badge.color} data-testid={badge.testId}>
            {badge.label}
          </Badge>
        )}
        {canCreateBackofficeAccount(status) && !readOnly && (
          <Button
            size="compact-xs"
            variant="light"
            loading={isCreating}
            onClick={onCreateAccount}
            data-testid="np-backoffice-create"
          >
            Create account
          </Button>
        )}
      </Group>
      <Text size="xs" c="dimmed">
        A Backoffice account is required before work can be dispatched to this partner. Creating one
        sends the partner an activation invite; dispatch is satisfied even before they activate.
      </Text>
    </Stack>
  );
}
